import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Power, RefreshCcw, History } from "lucide-react";
import { toast } from "sonner";
import SyncFullBayDialog from "../../components/SyncFullBayDialog";
import BackfillHistoryDialog from "../../components/BackfillHistoryDialog";

const EMPTY = { name: "", code: "", active: true, fullbay_customer_title: "" };

export default function CompaniesAdmin() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [syncCompany, setSyncCompany] = useState(null);
  const [backfillCompany, setBackfillCompany] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/companies", { params: { active_only: false } });
      setCompanies(data);
    } catch (e) { setError(extractError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormError(""); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      code: c.code,
      active: c.active,
      fullbay_customer_title: c.fullbay_customer_title || "",
    });
    setFormError("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setFormError("");
    try {
      if (editing) {
        await api.patch(`/companies/${editing.id}`, form);
        toast.success("Company updated");
      } else {
        await api.post("/companies", form);
        toast.success("Company created");
      }
      setOpen(false); load();
    } catch (e) { setFormError(extractError(e)); } finally { setSubmitting(false); }
  };

  const deactivate = async (c) => {
    if (!window.confirm(`Deactivate ${c.code}?`)) return;
    try { await api.delete(`/companies/${c.id}`); toast.success("Company deactivated"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const openSync = (c) => {
    if (!c.fullbay_customer_title) return;
    setSyncCompany(c);
  };

  return (
    <div data-testid="companies-admin">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Companies</h1>
          <p className="mt-1 text-sm text-slate-500">Tag every expense with the company that owns it.</p>
        </div>
        <Button onClick={openCreate} data-testid="create-company-btn" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
          <Plus className="mr-1 h-4 w-4" /> New company
        </Button>
      </div>

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="companies-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Code</TableHead>
              <TableHead>FullBay Customer Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : companies.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-slate-500" data-testid="companies-empty">
                No companies yet. Create one to start tagging expenses.
              </TableCell></TableRow>
            ) : companies.map((c) => (
              <TableRow key={c.id} data-testid={`company-row-${c.code}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="font-mono text-xs">{c.code}</TableCell>
                <TableCell data-testid={`company-fullbay-${c.code}`}>
                  {c.fullbay_customer_title ? (
                    <span className="font-mono text-xs text-slate-700">{c.fullbay_customer_title}</span>
                  ) : (
                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 hover:bg-amber-50" data-testid={`company-fullbay-unmapped-${c.code}`}>
                      Not mapped
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{c.active
                  ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Active</Badge>
                  : <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openSync(c)}
                    disabled={!c.fullbay_customer_title}
                    title={c.fullbay_customer_title ? "Sync FullBay repair invoices for this company" : "Set FullBay Customer Title first"}
                    data-testid={`company-sync-fullbay-${c.code}`}
                  >
                    <RefreshCcw className="h-4 w-4 text-slate-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setBackfillCompany(c)}
                    disabled={!c.fullbay_customer_title}
                    title={c.fullbay_customer_title ? "Backfill FullBay history for repeat-repair detection" : "Set FullBay Customer Title first"}
                    data-testid={`admin-backfill-${c.code}`}
                  >
                    <History className="h-4 w-4 text-slate-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-company-${c.code}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {c.active && (
                    <Button variant="ghost" size="sm" onClick={() => deactivate(c)} data-testid={`deactivate-company-${c.code}`}>
                      <Power className="h-4 w-4 text-rose-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="company-dialog">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit company" : "New company"}</DialogTitle>
              <DialogDescription>Short code is uppercase and unique, e.g. AFT or PGL.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="company-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Code</Label>
              <Input required value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={40} data-testid="company-code-input" />
              <p className="text-xs text-slate-500">Short code, e.g. AFT or PGL.</p>
            </div>
            <div className="space-y-2">
              <Label>FullBay Customer Title</Label>
              <Input value={form.fullbay_customer_title}
                onChange={(e) => setForm((f) => ({ ...f, fullbay_customer_title: e.target.value }))}
                maxLength={200}
                placeholder="e.g. ALL FREIGHT TRANS INC"
                data-testid="company-fullbay-input" />
              <p className="text-xs text-slate-500">
                Exact customer title from FullBay — used to match imported repair invoices to this company. Leave blank if unmapped.
              </p>
            </div>
            {editing && (
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div><Label>Active</Label><p className="text-xs text-slate-500">Inactive companies are hidden from dropdowns.</p></div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="company-active-switch" />
              </div>
            )}
            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="company-submit-btn" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {submitting ? "Saving…" : editing ? "Save changes" : "Create company"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SyncFullBayDialog
        companies={syncCompany ? [syncCompany] : []}
        presetCompanyId={syncCompany?.id || null}
        open={!!syncCompany}
        onOpenChange={(v) => { if (!v) setSyncCompany(null); }}
      />

      <BackfillHistoryDialog
        company={backfillCompany}
        open={!!backfillCompany}
        onOpenChange={(v) => { if (!v) setBackfillCompany(null); }}
      />
    </div>
  );
}
